import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { presenceManager } from '@/lib/presenceManager';
import { RealtimePresenceState } from '@supabase/supabase-js';

interface UserPresence {
  user_id: string;
  online_at: string;
}

type PresenceState = Record<string, UserPresence>;

type PresenceContextType = {
  trackUserPresence: (userId: string) => void;
  untrackUserPresence: (userId: string) => void;
  isUserOnline: (userId: string) => boolean;
  getLastSeenTime: (userId: string) => string | null;
};

const PresenceContext = createContext<PresenceContextType | undefined>(undefined);

export const usePresence = () => {
  const context = useContext(PresenceContext);
  if (!context) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
};

export const PresenceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [presenceState, setPresenceState] = useState<PresenceState>({});
  const trackedUsersRef = useRef<Set<string>>(new Set());
  const channelsRef = useRef<Map<string, ReturnType<typeof presenceManager.getChannel>>>(new Map());

  const handlePresenceSync = (userId: string, state: RealtimePresenceState<UserPresence>) => {
    const presences = Object.values(state);
    const normalized: PresenceState = {};
    
    presences.forEach(userPresences => {
      userPresences.forEach((presence: UserPresence) => {
        if (
          !normalized[presence.user_id] ||
          new Date(normalized[presence.user_id].online_at) < new Date(presence.online_at)
        ) {
          normalized[presence.user_id] = presence;
        }
      });
    });

    // Only update state if there are actual changes
    setPresenceState(prev => {
      // Check if anything has actually changed
      let hasChanges = false;
      
      for (const userId in normalized) {
        if (!prev[userId] || 
            prev[userId].online_at !== normalized[userId].online_at) {
          hasChanges = true;
          break;
        }
      }
      
      // If no changes, return the previous state to avoid re-renders
      if (!hasChanges) return prev;
      
      // Otherwise, return the updated state
      return {
        ...prev,
        ...normalized
      };
    });
  };

  const trackUserPresence = useCallback((userId: string) => {
    if (!userId) {
      console.error('[PresenceContext] Attempted to track presence with empty userId');
      return;
    }
    
    // If already tracking this user or subscription is pending, don't try to track again
    if (trackedUsersRef.current.has(userId) || presenceManager.isPendingSubscription(userId)) {
      return;
    }

    console.log(`[PresenceContext] Starting to track presence for user ${userId}`);
    trackedUsersRef.current.add(userId);

    // If channel is already subscribed, just return
    if (presenceManager.isChannelSubscribed(userId)) {
      console.log(`[PresenceContext] Channel for user ${userId} already subscribed`);
      return;
    }

    try {
      // Mark subscription as pending to prevent duplicate subscription attempts
      presenceManager.setPendingSubscription(userId, true);
      
      const channel = presenceManager.getChannel(userId);
      channelsRef.current.set(userId, channel);

      channel
        .on('presence', { event: 'join' }, ({ key, newPresences }) => {
          console.log(`[PresenceContext] User ${key} joined the presence channel`, newPresences);
        })
        .on('presence', { event: 'sync' }, () => {
          console.log(`[PresenceContext] Presence sync for user ${userId}`);
          const state = channel.presenceState<UserPresence>();
          handlePresenceSync(userId, state);
        })
        .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
          console.log(`[PresenceContext] User with key ${key} left the presence channel`);
          
          // Extract the user_id from the leftPresences
          if (leftPresences && leftPresences.length > 0) {
            const leftUserId = leftPresences[0].user_id;
            console.log(`[PresenceContext] User ${leftUserId} left the presence channel`);
            
            setPresenceState(prev => {
              const newState = { ...prev };
              delete newState[leftUserId];
              return newState;
            });
          }
        })
        .subscribe(async (status, error) => {
          if (status === 'SUBSCRIBED' && user) {
            console.log(`[PresenceContext] Successfully subscribed to presence for user ${userId}`);
            presenceManager.setChannelSubscribed(userId, true);
            try {
              await channel.track({
                user_id: user.id,
                online_at: new Date().toISOString(),
              });
            } catch (trackError) {
              console.error(`[PresenceContext] Error tracking presence for user ${userId}:`, trackError);
            }
          } else if (error) {
            console.error(`[PresenceContext] Error subscribing to presence for user ${userId}:`, error);
            presenceManager.setPendingSubscription(userId, false);
          }
        });
    } catch (error) {
      console.error(`[PresenceContext] Error setting up presence tracking for user ${userId}:`, error);
      presenceManager.setPendingSubscription(userId, false);
      trackedUsersRef.current.delete(userId);
    }
  }, [user]);

  const untrackUserPresence = useCallback((userId: string) => {
    if (!userId || !trackedUsersRef.current.has(userId)) return;

    console.log(`[PresenceContext] Stopping tracking presence for user ${userId}`);
    trackedUsersRef.current.delete(userId);
    presenceManager.releaseChannel(userId);
    channelsRef.current.delete(userId);
  }, []);

  useEffect(() => {
    return () => {
      trackedUsersRef.current.forEach(userId => {
        presenceManager.releaseChannel(userId);
      });
      trackedUsersRef.current.clear();
      channelsRef.current.clear();
    };
  }, []);

  const isUserOnline = (userId: string): boolean => {
    return !!presenceState[userId];
  };

  const getLastSeenTime = (userId: string): string | null => {
    return presenceState[userId]?.online_at || null;
  };

  return (
    <PresenceContext.Provider
      value={{
        trackUserPresence,
        untrackUserPresence,
        isUserOnline,
        getLastSeenTime
      }}
    >
      {children}
    </PresenceContext.Provider>
  );
};
